import { useCallback, useMemo } from 'react';
import { CLIP_UPDATE_TYPE } from '../../../../clipUpdateDataUtils/constants';
import { updateClipData } from '../../../../clipUpdateDataUtils';
import { useSpeakerDetailsForBroadcast } from '@/App/remotion/MainPlayer/stores/speakerDetailsStore';
import { getSpeakerNameAndTitle } from '@/Pages/Clip/CompositePlayer/SpeakerVideoUtils';
import { Scene } from '@/App/remotion/MainPlayer/types/Scene';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { ORIGINAL_VIDEO_ID } from '@/App/remotion/MainPlayer/utils/playerConfig/compositeSegment/constants';
import { isLayoutDefault } from '@/libs/layout';
import {
  getRecordingSpeakerKey,
  isRecordingScreenshare,
  isRecordingSharedVideo,
  isRecordingUserFeed,
  useVideoDetails
} from '@/App/remotion/MainPlayer/stores/videoSpeakerDetailsStore';
import { sharedVideoBarsColors } from '@/context/TranscriptContext/SpeakerColors';

export type SpeakerTimelineTrack = Pick<SpeakerWithDetails, 'bgColor' | 'lightBgColor' | 'color'> & {
  id: string;
  name: string;
  selected: boolean;
};

enum SpeacialTrackId {
  SCREENSHARE = 'SCREENSHARE',
  SHARED_VIDEO = 'SHARED_VIDEO'
}

const SCREENSHARE_TRACK_NAME = 'Shared Screen';
const SHARED_VIDEO_TRACK_NAME = 'Shared Video';

export const getTracksForScene = (scene: Scene, tracks: SpeakerTimelineTrack[]) => {
  if (isLayoutDefault(scene.details.layout)) {
    return tracks.filter(track => track.id === ORIGINAL_VIDEO_ID);
  }
  return tracks.filter(track => track.id !== ORIGINAL_VIDEO_ID);
};

export function useSceneTracks({
  broadcastId,
  clipId,
  sceneId,
  scenes,
  compositeSegmentId
}: {
  broadcastId: string;
  clipId: string;
  sceneId: string;
  scenes: Scene[];
  compositeSegmentId: string;
}) {
  const videoIdToSpeakerDetails = useVideoDetails();

  const currentScene = useMemo(() => scenes.find(scene => scene.id === sceneId), [scenes, sceneId]);
  const visibleVideoIds = useMemo(() => currentScene?.details.visible_video_ids || [], [currentScene]);
  const allVideoIds = useMemo(() => currentScene?.details.all_video_ids || [], [currentScene]);
  const speakerDetails = useSpeakerDetailsForBroadcast(broadcastId);

  const tracks = useMemo(() => {
    return allVideoIds
      .map(videoId => {
        const speakerKey = getRecordingSpeakerKey({
          broadcastId,
          recordingId: videoId,
          snapshot: videoIdToSpeakerDetails
        });
        const isUserRecording = isRecordingUserFeed({
          broadcastId,
          recordingId: videoId,
          snapshot: videoIdToSpeakerDetails
        });
        const isSharedVideo = isRecordingSharedVideo({
          broadcastId,
          recordingId: videoId,
          snapshot: videoIdToSpeakerDetails
        });
        const speaker = speakerDetails[speakerKey ?? ''];

        let name = SCREENSHARE_TRACK_NAME;
        if (isUserRecording && speaker) {
          // this is needed because even screenshare can have a speaker
          const speakerDetails = getSpeakerNameAndTitle(speaker);
          name = speakerDetails.name;
        } else if (videoId === ORIGINAL_VIDEO_ID) {
          name = 'Original track';
        } else if (isSharedVideo) {
          name = SHARED_VIDEO_TRACK_NAME;
        }

        return {
          id: videoId,
          name: name,
          selected: visibleVideoIds.includes(videoId),
          bgColor: speaker?.bgColor || 'bg-slate-100',
          lightBgColor: speaker?.lightBgColor || 'bg-slate-100',
          color: speaker?.color || 'text-slate-700'
        };
      })
      .filter(a => a !== null) as SpeakerTimelineTrack[];
  }, [videoIdToSpeakerDetails, visibleVideoIds, allVideoIds, speakerDetails, broadcastId]);

  const tracksForCurrentScene = useMemo(() => {
    if (!currentScene) {
      return tracks;
    }
    return getTracksForScene(currentScene, tracks);
  }, [currentScene, tracks]);

  const tracksToRender = useMemo(() => {
    const screenshareTracks: SpeakerTimelineTrack[] = [];
    const sharedVideoTracks: SpeakerTimelineTrack[] = [];
    const userVideoTracks: SpeakerTimelineTrack[] = [];

    tracksForCurrentScene.forEach(track => {
      if (isRecordingScreenshare({ broadcastId, recordingId: track.id, snapshot: videoIdToSpeakerDetails })) {
        screenshareTracks.push(track);
      } else if (isRecordingSharedVideo({ broadcastId, recordingId: track.id, snapshot: videoIdToSpeakerDetails })) {
        sharedVideoTracks.push(track);
      } else {
        userVideoTracks.push(track);
      }
    });

    const finalTracks: SpeakerTimelineTrack[] = [...userVideoTracks];

    // If there are screenshare tracks, combine them into one
    if (screenshareTracks.length > 0) {
      const allScreenshareSelected = screenshareTracks.every(track => track.selected);
      const combinedScreenshare: SpeakerTimelineTrack = {
        id: SpeacialTrackId.SCREENSHARE,
        name: SCREENSHARE_TRACK_NAME,
        selected: allScreenshareSelected,
        bgColor: 'bg-slate-100',
        lightBgColor: 'bg-slate-100',
        color: 'text-slate-700'
      };
      finalTracks.push(combinedScreenshare);
    }

    if (sharedVideoTracks.length > 0) {
      const allSharedVideoSelected = sharedVideoTracks.every(track => track.selected);
      const { bgColor, lightBgColor, textColor: color } = sharedVideoBarsColors;
      const combinedSharedVideo: SpeakerTimelineTrack = {
        id: SpeacialTrackId.SHARED_VIDEO,
        name: SHARED_VIDEO_TRACK_NAME,
        selected: allSharedVideoSelected,
        bgColor,
        lightBgColor,
        color
      };
      finalTracks.push(combinedSharedVideo);
    }

    return finalTracks;
  }, [videoIdToSpeakerDetails, tracksForCurrentScene, broadcastId]);

  const handleTrackToggle = useCallback(
    (toggledTrackName: string) => {
      if (!currentScene) {
        return;
      }

      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY_FROM_SCENE,
        sceneId: currentScene.id,
        trackName: toggledTrackName,
        compositeId: compositeSegmentId
      });
    },
    [clipId, currentScene, compositeSegmentId]
  );

  return {
    tracksToRender,
    handleTrackToggle
  };
}
